import { Fragment, useEffect, useRef, useState } from "react";
import { ArrowLeft, Info, MailOpen, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";
import { Bubble } from "./Bubble";
import { Composer } from "./Composer";
import { TemplateDialog } from "./TemplateDialog";
import { initials, dayLabel, windowLeft, WA_BG } from "./util";

function Header({ conv, onBack, onInfo, onConvChange, onUnread }) {
  const toggle = () => api.patch(`/wa-inbox/conversations/${conv.phone}`, { status: conv.status === "closed" ? "open" : "closed" })
    .then(({ data }) => { onConvChange(data); toast.success(data.status === "closed" ? "Chat closed" : "Chat reopened"); })
    .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  const left = windowLeft(conv.window_expires_at);
  return (
    <div className="h-[60px] px-3 sm:px-4 bg-[#f0f2f5] flex items-center gap-3 flex-shrink-0 border-l border-slate-200">
      <button onClick={onBack} className="md:hidden text-[#54656f]" data-testid="inbox-back-btn"><ArrowLeft className="w-5 h-5" /></button>
      <button onClick={onInfo} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid="inbox-chat-header">
        <div className="w-10 h-10 rounded-full bg-[#dfe5e7] text-[#54656f] flex items-center justify-center font-semibold flex-shrink-0">{initials(conv.display_name)}</div>
        <div className="min-w-0">
          <div className="font-medium text-[#111b21] truncate" data-testid="inbox-chat-name">{conv.display_name}</div>
          <div className="text-xs truncate" data-testid="inbox-window-status">
            <span className="text-[#667781] hidden sm:inline">+{conv.phone} · </span>
            {conv.window_open ? <span className="text-[#008069]"><span className="hidden sm:inline">Reply window open · </span>{left}<span className="sm:hidden"> to reply</span></span> : <span className="text-amber-700">Window closed<span className="hidden sm:inline"> — template only</span></span>}
          </div>
        </div>
      </button>
      <button onClick={onUnread} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center text-[#54656f]" title="Mark as unread" data-testid="inbox-mark-unread-btn"><MailOpen className="w-5 h-5" /></button>
      <button onClick={toggle} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center text-[#54656f]" title={conv.status === "closed" ? "Reopen chat" : "Close chat"} data-testid="inbox-close-chat-btn">
        {conv.status === "closed" ? <RotateCcw className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
      </button>
      <button onClick={onInfo} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center text-[#54656f]" title="Contact info" data-testid="inbox-info-btn"><Info className="w-5 h-5" /></button>
    </div>
  );
}

export function ChatView({ phone, onBack, onInfo, onConvChange, draft, setDraft, refreshList, conv, setConv }) {
  const [msgs, setMsgs] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const endRef = useRef(null);
  const lastCount = useRef(0);

  const load = async (first) => {
    try {
      const { data } = await api.get(`/wa-inbox/conversations/${phone}`);
      setConv(data.conversation); setMsgs(data.messages);
      if (data.conversation.unread) { await api.post(`/wa-inbox/conversations/${phone}/read`); refreshList(); }
    } catch (e) { if (first === true) toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    setMsgs(null); setReplyTo(null); lastCount.current = 0;
    load(true);
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);
  useEffect(() => {
    if (msgs && msgs.length !== lastCount.current) { endRef.current?.scrollIntoView({ block: "end" }); lastCount.current = msgs.length; }
  }, [msgs]);

  if (!conv || conv.phone !== phone || !msgs) return <div className="flex-1 flex items-center justify-center" style={WA_BG}><Loader2 className="w-7 h-7 animate-spin text-[#00a884]" /></div>;
  const unread = () => api.patch(`/wa-inbox/conversations/${phone}`, { unread: 1 }).then(() => { toast.success("Marked as unread"); refreshList(); onBack(); });

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full">
      <Header conv={conv} onBack={onBack} onInfo={onInfo} onConvChange={onConvChange} onUnread={unread} />
      <div className="flex-1 overflow-y-auto py-3" style={WA_BG} data-testid="inbox-messages">
        {msgs.length === 0 && <div className="mx-auto mt-6 w-fit bg-[#fff8c5] text-[#54656f] text-xs rounded-lg px-3 py-1.5 shadow-sm">No messages yet. Start with a template.</div>}
        {msgs.map((m, i) => {
          const day = m.at && dayLabel(m.at);
          const showDay = i === 0 || day !== dayLabel(msgs[i - 1].at);
          return (
            <Fragment key={m.id}>
              {showDay && <div className="flex justify-center my-2.5"><span className="bg-white text-[#54656f] text-[12px] rounded-lg px-3 py-1 shadow-sm uppercase">{day}</span></div>}
              <Bubble m={m} onReply={setReplyTo} />
            </Fragment>
          );
        })}
        <div ref={endRef} className="h-2" />
      </div>
      <Composer conv={conv} replyTo={replyTo} clearReply={() => setReplyTo(null)} onSent={() => { load(); refreshList(); }}
        onTemplate={() => setTplOpen(true)} draft={draft} setDraft={setDraft} />
      <TemplateDialog open={tplOpen} onClose={() => setTplOpen(false)} phone={phone} onSent={() => { load(); refreshList(); }} />
    </div>
  );
}
